// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { FormsModule } from '@angular/forms';

import { SharedModule } from '@app/shared';

import { IAMEffects, reducers, ACCESS_MANAGEMENT_FEATURE_NAME } from './state';
import { AccessManagementRoutingModule } from './access-management-routing.module';
import { DateTimePipe } from './date-time.pipe';
import { UserRolesPipe } from './user-roles.pipe';
import { RolesListComponent } from './roles/roles-list/roles-list.component';
import { RemoveRoleModalComponent } from './roles/roles-list/remove-role-modal/remove-role-modal.component';
import { RoleDetailsComponent } from './roles/role-details/role-details.component';
import { RoleUsersTableComponent } from './roles/role-users-table/role-users-table.component';
import { RolePoliciesTableComponent } from './roles/role-policies-table/role-policies-table.component';
import { RoleDetailsCardComponent } from './roles/role-details-card/role-details-card.component';
import { RoleUsersFormComponent } from './roles/role-users-form/role-users-form.component';
import { RoleScopeFormComponent } from './roles/role-scope-form/role-scope-form.component';
import { RoleCreateNewComponent } from './roles/role-create-new/role-create-new.component';
import { SelectableActionTableComponent } from './selectable-action-table/selectable-action-table.component';
import { RoleScopeFormGuardService } from './role-scope-form-guard.service';
import { RadioSelectComponent } from './radio-select/radio-select.component';
import { DeleteModalComponent } from './roles/role-details/delete-modal/delete-modal.component';
import { RolePoliciesFormComponent } from './roles/role-policies-form/role-policies-form.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ManageUserScopesComponent } from './roles/manage-user-scopes/manage-user-scopes.component';
import { UserDetailCardComponent } from './user-detail-card/user-detail-card.component';
import { RoleScopeOptionsComponent } from './roles/role-scope-options/role-scope-options.component';
import { GroupsListComponent } from './groups/groups-list/groups-list.component';
import { GroupCreateComponent } from './groups/group-create/group-create.component';
import { GroupDeleteModalComponent } from './groups/group-delete-modal/group-delete-modal.component';
import { RoleScopeCardComponent } from './roles/role-scope-card/role-scope-card.component';
import { GroupDetailsComponent } from './groups/group-details/group-details.component';
import { GroupDetailsCardComponent } from './groups/group-details/group-details-card/group-details-card.component';
import { GroupDetailsUsersTabComponent } from './groups/group-details/group-details-users-tab/group-details-users-tab.component';
import { GroupUsersFormComponent } from './groups/group-details/group-users-form/group-users-form.component';
import { GroupDetailsRolesTabComponent } from './groups/group-details/group-details-roles-tab/group-details-roles-tab.component';
import { GroupRolesFormComponent } from './groups/group-details/group-roles-form/group-roles-form.component';
import { GroupEditComponent } from './groups/group-edit/group-edit.component';
import { GeneralDeleteModalComponent } from './shared/delete-modal/delete-modal.component';
import { RoleDetailsGroupsTabComponent } from './roles/role-details/role-details-groups-tab/role-details-groups-tab.component';
import { RoleDetailsGroupsAddComponent } from './roles/role-details/role-details-groups-add/role-details-groups-add.component';
// tslint:disable-next-line:max-line-length
import { RoleDetailsGroupAddScopeComponent } from './roles/role-details/role-details-group-add-scope/role-details-group-add-scope.component';
import { GroupFacadeService } from './state/groups/group.facade';
import { GroupDetailsFacadeService } from './state/group-details/group-details.facade';
import { UsersFacadeService } from './state/users.facade';
import { PoliciesFacadeService } from './state/policies.facade';
import { PolicyDetailsFacadeService } from './state/policy-details/policy-details.facade';
import { PolicyStatementFacadeService } from './state/policy-statement.facade';
import { UsersComponent } from './users/users.component';
import { UsersListComponent } from './users/users-list/users-list.component';
import { UserDetailsComponent } from './users/user-details/user-details.component';
import { UserDetailsFacadeService } from './state/user-details/user-details.facade';
import { UserDetailsCardComponent } from './users/user-details/user-details-card/user-details-card.component';
import { UserDetailsRolesTabComponent } from './users/user-details/user-details-roles-tab/user-details-roles-tab.component';
import { UserDetailsRolesTabAddComponent } from './users/user-details/user-details-roles-tab-add/user-details-roles-tab-add.component';
import { RolesFacadeService } from './state/roles.facade';
import { RoleScopeFacadeService } from './state/role-scole.facade';
import { UserDetailsGroupsTabComponent } from './users/user-details/user-details-groups-tab/user-details-groups-tab.component';
import { UserDetailsGroupsTabAddComponent } from './users/user-details/user-details-groups-tab-add/user-details-groups-tab-add.component';
import { PoliciesComponent } from './policies/policies.component';
import { PoliciesListComponent } from './policies/policies-list/policies-list.component';
import { PolicyCreateNewComponent } from './policies/policy-create-new/policy-create-new.component';
import { RemovePolicyModalComponent } from './policies/policies-list/remove-policy-modal/remove-policy-modal.component';
import { ReviewNewPolicyComponent } from './review-new-policy/review-new-policy.component';
import { PolicyStatementCardComponent } from './policies/policy-statement-card/policy-statement-card.component';
import { PolicyEditComponent } from './policies/policy-edit/policy-edit.component';
// tslint:disable-next-line:max-line-length
import { PolicyDetailsCardComponent } from './policies/policies-list/policy-details/policy-details-card/policy-details-card.component';
import { PolicyDetailsComponent } from './policies/policies-list/policy-details/policy-details.component';
// tslint:disable-next-line:max-line-length
import { PolicyStatementsListComponent } from './policies/policies-list/policy-details/policy-statements-list/policy-statements-list.component';
// tslint:disable-next-line:max-line-length
import { PolicyStatementAddComponent } from './policies/policies-list/policy-details/policy-statements-list/policy-statement-add/policy-statement-add.component';
// tslint:disable-next-line:max-line-length
import { PolicyStatementEditComponent } from './policies/policies-list/policy-details/policy-statements-list/policy-statement-edit/policy-statement-edit.component';
import { SelectableFieldComponent } from './shared/selectable-field/selectable-field.component';
import { RoleDetailsFacadeService } from './state/role-details.facade';
// tslint:disable-next-line:max-line-length
import { RoleDetailsGroupScopesComponent } from './roles/role-details/role-details-groups-tab/role-details-group-scopes/role-details-group-scopes.component';
// tslint:disable-next-line:max-line-length
import { UserDetailsRoleScopesComponent } from './users/user-details/user-details-roles-tab/user-details-role-scopes/user-details-role-scopes.component';
import { RoleEditComponent } from './roles/role-edit/role-edit.component';
import { UserDetailsRowComponent } from './users/user-details/user-details-row/user-details-row.component';
import { OrganizationsComponent } from './organizations/organizations.component';
import { OrganizationsListComponent } from './organizations/organizations-list/organizations-list.component';
import { OrganizationRolesListComponent } from './organizations/organization-roles-list/organization-roles-list.component';
import { OrganizationRoleUsersListComponent } from './organizations/organization-role-users-list/organization-role-users-list.component';
// tslint:disable-next-line:max-line-length
import { OrganizationRoleManageUserScopesComponent } from './organizations/organization-role-manage-user-scopes/organization-role-manage-user-scopes.component';
import { OrganizationsFacadeService } from './state/organizations.facade';
import { OrganizationDetailsFacadeService } from './state/organization-details/organization-details.facade';
import { ProjectCommitteeHandlerComponent } from './project-comittee-handler/project-comittee-handler.component';

@NgModule({
  imports: [
    FormsModule,
    CommonModule,
    SharedModule,
    AccessManagementRoutingModule,
    NgxDatatableModule,
    NgbModule,
    EffectsModule.forFeature(IAMEffects),
    StoreModule.forFeature(ACCESS_MANAGEMENT_FEATURE_NAME, reducers)
  ],
  declarations: [
    RolesListComponent,
    RoleDetailsComponent,
    DateTimePipe,
    UserRolesPipe,
    RoleUsersTableComponent,
    RolePoliciesTableComponent,
    RoleDetailsCardComponent,
    RoleUsersFormComponent,
    RoleScopeFormComponent,
    RoleCreateNewComponent,
    SelectableActionTableComponent,
    RadioSelectComponent,
    DeleteModalComponent,
    PoliciesListComponent,
    PolicyCreateNewComponent,
    PolicyEditComponent,
    PolicyStatementCardComponent,
    ReviewNewPolicyComponent,
    RemoveRoleModalComponent,
    RolePoliciesFormComponent,
    RemovePolicyModalComponent,
    UsersListComponent,
    DashboardComponent,
    ManageUserScopesComponent,
    UserDetailCardComponent,
    RoleScopeOptionsComponent,
    GroupsListComponent,
    GroupCreateComponent,
    GroupDeleteModalComponent,
    RoleScopeCardComponent,
    GroupDetailsComponent,
    GroupDetailsCardComponent,
    GroupDetailsUsersTabComponent,
    GroupUsersFormComponent,
    GroupDetailsRolesTabComponent,
    GroupRolesFormComponent,
    GroupEditComponent,
    GeneralDeleteModalComponent,
    RoleDetailsGroupsTabComponent,
    RoleDetailsGroupsAddComponent,
    RoleDetailsGroupAddScopeComponent,
    UsersComponent,
    UserDetailsComponent,
    UserDetailsCardComponent,
    UserDetailsRolesTabComponent,
    UserDetailsRolesTabAddComponent,
    UserDetailsGroupsTabComponent,
    UserDetailsGroupsTabAddComponent,
    PolicyStatementsListComponent,
    PolicyDetailsComponent,
    PolicyDetailsCardComponent,
    PoliciesComponent,
    PolicyStatementAddComponent,
    PolicyStatementEditComponent,
    SelectableFieldComponent,
    RoleDetailsGroupScopesComponent,
    UserDetailsRoleScopesComponent,
    RoleEditComponent,
    UserDetailsRowComponent,
    OrganizationsComponent,
    OrganizationsListComponent,
    OrganizationRolesListComponent,
    OrganizationRoleUsersListComponent,
    OrganizationRoleManageUserScopesComponent,
    ProjectCommitteeHandlerComponent,
  ],
  providers: [
    RoleScopeFormGuardService,
    GroupFacadeService,
    GroupDetailsFacadeService,
    UsersFacadeService,
    PoliciesFacadeService,
    PolicyDetailsFacadeService,
    PolicyStatementFacadeService,
    UserDetailsFacadeService,
    RolesFacadeService,
    RoleScopeFacadeService,
    RoleDetailsFacadeService,
    OrganizationsFacadeService,
    OrganizationDetailsFacadeService
  ],
  entryComponents: [
    GeneralDeleteModalComponent,
    DeleteModalComponent,
    RemoveRoleModalComponent,
    RemovePolicyModalComponent,
    GroupDeleteModalComponent
  ]
})
export class AccessManagementModule { }



// WEBPACK FOOTER //
// ./src/app/access-management/access-management.module.ts